import { QueryHookOptions, useQuery } from '@provision/graphql-client';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { isNullish } from 'remeda';
import {
  PartnerHolidayEntity,
  PartnerHolidayQueryResponse,
} from 'src/graphql/entities/partner-holiday/partner-holiday.types';
import { GET_PARTNER_HOLIDAY_QUERY } from 'src/graphql/entities/partner-holiday/partner-holidays.operations';
import { ConditionInput, QueryVariables, Records } from 'src/graphql/types';

export interface GetPartnerHolidayQuery {
  getPartnerHoliday: Records<PartnerHolidayQueryResponse>;
}

export function usePartnerHolidays(
  params?: {
    partnerId?: number;
    limit?: number;
    filters: ConditionInput[];
  },
  options?: QueryHookOptions<GetPartnerHolidayQuery>,
) {
  const { partnerId, limit, filters = [] } = params ?? {};

  const extraFilters = useMemo(() => {
    const baseFilters: ConditionInput[] = [];

    if (!isNullish(partnerId)) {
      baseFilters.push({
        attribute: 'partnerId',
        operator: '=',
        value: partnerId.toString(),
      });
    }

    return baseFilters;
  }, [partnerId]);

  const { data, ...rest } = useQuery<GetPartnerHolidayQuery, QueryVariables>(
    GET_PARTNER_HOLIDAY_QUERY,
    {
      ...options,
      variables: {
        params: { filters: [...extraFilters, ...filters], limit },
      },
    },
  );

  const partnerHolidays = useMemo(
    (): PartnerHolidayEntity[] =>
      (data?.getPartnerHoliday?.records || []).map((value) => ({
        ...value,
        holidayStart: dayjs(value.holidayStart),
        holidayEnd: dayjs(value.holidayEnd),
      })),
    [data?.getPartnerHoliday],
  );

  return {
    partnerHolidays,
    ...rest,
  };
}
